// Formulario de creación de cotización. Soporta:
//  · líneas dinámicas (add/remove)
//  · selección de plantilla (carga sus líneas como seed)
//  · creación standalone (sin lead) o desde lead_id
// El lead picker queda fuera de scope de v0.1 — se acepta lead_id por input
// de texto para que el usuario pegue el UUID si viene del módulo CRM.

import { useEffect, useState } from 'react';

import { useCreateQuote, useQuoteTemplates } from '../hooks/useQuotes';
import type { Quote, QuoteLineItemIn } from '../types';

interface Props {
  defaultLeadId?: string;
  onCreated?: (quote: Quote) => void;
}

const EMPTY_LINE: QuoteLineItemIn = {
  description: '',
  qty: '1',
  unit_price: '0',
  tax_rate: '16',
  sort_order: 0,
};

export function QuoteForm({ defaultLeadId = '', onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [currency, setCurrency] = useState('MXN');
  const [validUntil, setValidUntil] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [leadId, setLeadId] = useState(defaultLeadId);
  const [templateId, setTemplateId] = useState('');
  const [lines, setLines] = useState<QuoteLineItemIn[]>([{ ...EMPTY_LINE }]);

  const { submit, loading, error } = useCreateQuote();
  const { data: templates } = useQuoteTemplates();
  const [success, setSuccess] = useState<string | null>(null);

  // Cargar líneas de la plantilla seleccionada como seed editable
  useEffect(() => {
    if (!templateId || !templates) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setCurrency(tpl.currency || currency);
    if (!title) setTitle(tpl.title || tpl.name);
    if (!notes) setNotes(tpl.notes);
    if (tpl.line_items.length > 0) {
      setLines(
        tpl.line_items.map((li) => ({
          description: li.description,
          qty: li.qty,
          unit_price: li.unit_price,
          tax_rate: li.tax_rate,
          sort_order: li.sort_order,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, templates]);

  const updateLine = (index: number, patch: Partial<QuoteLineItemIn>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, { ...EMPTY_LINE, sort_order: prev.length }]);
  const removeLine = (index: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const subtotal = lines.reduce(
    (acc, l) => acc + Number(l.qty || 0) * Number(l.unit_price || 0),
    0,
  );
  const tax = lines.reduce((acc, l) => {
    const lineSub = Number(l.qty || 0) * Number(l.unit_price || 0);
    return acc + (lineSub * Number(l.tax_rate || 0)) / 100;
  }, 0);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    const res = await submit({
      title,
      client_name: clientName,
      client_email: clientEmail,
      client_phone: clientPhone,
      client_company: clientCompany,
      currency,
      valid_until: validUntil || null,
      notes,
      lead_id: leadId || null,
      template_id: templateId || null,
      line_items: lines.filter((l) => l.description.trim().length > 0),
    });
    if (res) {
      setSuccess(`Cotización ${res.id.slice(0, 8)} creada — total ${res.currency} ${res.total}`);
      onCreated?.(res);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4 p-4 border rounded-md max-w-3xl">
      <h3 className="text-base font-semibold">Nueva cotización</h3>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-muted-foreground">Título</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Plantilla (opcional)</span>
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          >
            <option value="">— sin plantilla —</option>
            {(templates || []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Cliente · nombre</span>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Empresa</span>
          <input
            type="text"
            value={clientCompany}
            onChange={(e) => setClientCompany(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Email</span>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Teléfono</span>
          <input
            type="tel"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Lead ID (opcional)</span>
          <input
            type="text"
            value={leadId}
            onChange={(e) => setLeadId(e.target.value)}
            placeholder="UUID del lead origen"
            className="mt-1 w-full border rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted-foreground">Vigencia hasta</span>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">Moneda</span>
          <input
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
            className="mt-1 w-full border rounded px-2 py-1 text-sm uppercase"
          />
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Líneas</h4>
          <button
            type="button"
            onClick={addLine}
            className="text-xs px-2 py-1 border rounded"
          >
            + Línea
          </button>
        </div>
        <table className="min-w-full text-sm mt-2">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b">
              <th className="px-2 py-1">Descripción</th>
              <th className="px-2 py-1 w-20">Cant.</th>
              <th className="px-2 py-1 w-28">Unitario</th>
              <th className="px-2 py-1 w-20">IVA %</th>
              <th className="px-2 py-1 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-b">
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.description}
                    onChange={(e) => updateLine(i, { description: e.target.value })}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={String(l.qty)}
                    onChange={(e) => updateLine(i, { qty: e.target.value })}
                    className="w-full border rounded px-2 py-1 text-sm text-right"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={String(l.unit_price)}
                    onChange={(e) => updateLine(i, { unit_price: e.target.value })}
                    className="w-full border rounded px-2 py-1 text-sm text-right"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="number"
                    step="0.01"
                    value={String(l.tax_rate ?? '16')}
                    onChange={(e) => updateLine(i, { tax_rate: e.target.value })}
                    className="w-full border rounded px-2 py-1 text-sm text-right"
                  />
                </td>
                <td className="px-2 py-1 text-center">
                  <button
                    type="button"
                    onClick={() => removeLine(i)}
                    disabled={lines.length === 1}
                    className="text-xs text-destructive disabled:opacity-30"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="text-xs text-muted-foreground mt-2 text-right">
          Subtotal: <span className="font-mono">{subtotal.toFixed(2)}</span> ·
          Impuestos: <span className="font-mono">{tax.toFixed(2)}</span> ·
          Total: <span className="font-mono font-semibold">{(subtotal + tax).toFixed(2)} {currency}</span>
        </div>
      </div>

      <label className="block">
        <span className="text-xs text-muted-foreground">Notas / términos</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="mt-1 w-full border rounded px-2 py-1 text-sm"
        />
      </label>

      {error && <div className="text-sm text-destructive">{error}</div>}
      {success && <div className="text-sm text-green-700">{success}</div>}

      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 text-sm rounded bg-black text-primary-foreground disabled:opacity-50"
      >
        {loading ? 'Creando…' : 'Crear cotización'}
      </button>
    </form>
  );
}
